import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type { ApiPrescriptionZone } from 'types/prescription';

// Flattens the staged FeatureCollection into the wire-ready ApiPrescriptionZone
// list. Every (Multi)Polygon feature contributes at least one zone:
//   - Polygon  → one zone with its rings as coordinates
//   - MultiPolygon → one zone per component polygon, all sharing the same rate
// Features whose selected column isn't a finite number are skipped.
export const toApiZones = ({
    featureCollection,
    rateColumn
}: {
    featureCollection: FeatureCollection;
    rateColumn: string;
}): ApiPrescriptionZone[] => {
    const zones: ApiPrescriptionZone[] = [];

    for (const feature of featureCollection.features) {
        const rate = readNumericProperty({
            properties: feature.properties,
            rateColumn
        });

        if (rate === null) {
            continue;
        }

        const { geometry } = feature;

        if (!geometry) {
            continue;
        }

        if (geometry.type === 'Polygon') {
            zones.push({
                coordinates: normalizeRings(geometry as Polygon),
                rate
            });

            continue;
        }

        if (geometry.type === 'MultiPolygon') {
            for (const polygonRings of (geometry as MultiPolygon).coordinates) {
                zones.push({
                    coordinates: polygonRings as number[][][],
                    rate
                });
            }
        }
    }

    return zones;
};

const normalizeRings = (polygon: Polygon): number[][][] => polygon.coordinates as number[][][];

const readNumericProperty = ({
    properties,
    rateColumn
}: {
    properties: FeatureCollection['features'][number]['properties'];
    rateColumn: string;
}): number | null => {
    if (!properties) {
        return null;
    }

    const raw = (properties as Record<string, unknown>)[rateColumn];

    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw;
    }

    if (typeof raw === 'string') {
        const parsed = Number(raw);

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};
